import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import Hls from 'hls.js';
import { API_URL, formatApiError } from '../utils/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import {
  VideoCamera, Plus, Trash, ArrowsClockwise, Play, Pause, WifiHigh, WifiSlash,
  WarningCircle, Spinner, Eye, SpeakerHigh, SpeakerX, Camera as CameraIcon,
  CornersOut, CornersIn, Microphone,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const authHeaders = () => {
  const t = localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/* ─── Single-camera HLS player with custom control bar ─────────────────── */
function HlsPlayer({ hlsUrl, onError, onRefresh }) {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const hlsRef = useRef(null);
  // Keep the latest onError in a ref so we can react to fatal errors without
  // tearing down the HLS instance every time the parent re-renders (which
  // otherwise aborts every in-flight manifest/segment XHR → black screen).
  const onErrorRef = useRef(onError);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [volume, setVolume] = useState(0.7);
  const [fullscreen, setFullscreen] = useState(false);
  const [live, setLive] = useState(false);

  // HLS setup: prefers native (Safari/iOS), falls back to hls.js.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !hlsUrl) return;

    if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }

    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = hlsUrl;
      video.play().catch(() => {});
    } else if (Hls.isSupported()) {
      const hls = new Hls({ enableWorker: false, lowLatencyMode: true });
      hlsRef.current = hls;
      hls.loadSource(hlsUrl);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => { setLive(true); video.play().catch(() => {}); });
      hls.on(Hls.Events.ERROR, (_, data) => {
        if (data?.fatal && onErrorRef.current) onErrorRef.current(data?.details || 'HLS playback error');
      });
    } else if (onErrorRef.current) {
      onErrorRef.current('HLS not supported in this browser');
    }

    return () => {
      if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
    };
    // Only re-run when the source URL actually changes — depending on
    // callback identity would tear the stream down on every parent re-render.
  }, [hlsUrl]);

  // Play/pause + volume + fullscreen listeners on the <video> element.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onVol = () => { setMuted(v.muted); setVolume(v.volume); };
    const onFs = () => setFullscreen(document.fullscreenElement === containerRef.current);
    v.addEventListener('play', onPlay);
    v.addEventListener('pause', onPause);
    v.addEventListener('volumechange', onVol);
    document.addEventListener('fullscreenchange', onFs);
    return () => {
      v.removeEventListener('play', onPlay);
      v.removeEventListener('pause', onPause);
      v.removeEventListener('volumechange', onVol);
      document.removeEventListener('fullscreenchange', onFs);
    };
  }, []);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => {}); else v.pause();
  };
  const toggleMute = () => { const v = videoRef.current; if (v) v.muted = !v.muted; };
  const changeVolume = (val) => {
    const v = videoRef.current; if (!v) return;
    v.volume = val; if (val > 0) v.muted = false;
  };
  const toggleFullscreen = async () => {
    const c = containerRef.current; if (!c) return;
    if (document.fullscreenElement) await document.exitFullscreen();
    else await c.requestFullscreen();
  };
  const captureSnapshot = () => {
    const v = videoRef.current; if (!v || !v.videoWidth) { toast.error('Video not ready'); return; }
    const canvas = document.createElement('canvas');
    canvas.width = v.videoWidth; canvas.height = v.videoHeight;
    canvas.getContext('2d').drawImage(v, 0, 0);
    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `snapshot-${Date.now()}.png`;
      a.click(); URL.revokeObjectURL(url);
    }, 'image/png');
    toast.success('Snapshot downloaded');
  };
  const talkNotice = () => toast.info('Two-way talk requires the Imou Talk API — coming soon.');

  return (
    <div ref={containerRef} className="relative w-full aspect-video rounded-xl overflow-hidden bg-black group" data-testid="surveillance-player">
      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        className="w-full h-full object-contain"
        data-testid="surveillance-video"
      />

      {live && (
        <div className="absolute top-3 left-3 flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-600 text-white text-[10px] font-bold tracking-widest">
          <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> LIVE
        </div>
      )}

      {/* Control bar — auto-hides after 2s of no hover in fullscreen */}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent px-3 pt-8 pb-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="flex items-center gap-3">
          <button type="button" onClick={togglePlay} className="text-white hover:text-[#0779BC] transition-colors" data-testid="player-playpause" title={playing ? 'Pause' : 'Play'}>
            {playing ? <Pause size={22} weight="fill" /> : <Play size={22} weight="fill" />}
          </button>

          <button type="button" onClick={toggleMute} className="text-white hover:text-[#0779BC] transition-colors" data-testid="player-mute" title={muted ? 'Unmute' : 'Mute'}>
            {muted || volume === 0 ? <SpeakerX size={22} weight="regular" /> : <SpeakerHigh size={22} weight="regular" />}
          </button>

          <input
            type="range" min="0" max="1" step="0.01"
            value={muted ? 0 : volume}
            onChange={(e) => changeVolume(parseFloat(e.target.value))}
            className="w-20 h-1 accent-[#0779BC] cursor-pointer"
            data-testid="player-volume"
            title="Volume"
          />

          <div className="flex-1" />

          <button type="button" onClick={captureSnapshot} className="text-white hover:text-[#0779BC] transition-colors" data-testid="player-snapshot" title="Snapshot">
            <CameraIcon size={20} weight="regular" />
          </button>

          <button type="button" onClick={talkNotice} className="text-white/50 hover:text-white/80 transition-colors" data-testid="player-talk" title="Two-way talk (coming soon)">
            <Microphone size={20} weight="regular" />
          </button>

          <button type="button" onClick={onRefresh} className="text-white hover:text-[#0779BC] transition-colors" data-testid="player-refresh" title="Refresh stream">
            <ArrowsClockwise size={20} weight="regular" />
          </button>

          <button type="button" onClick={toggleFullscreen} className="text-white hover:text-[#0779BC] transition-colors" data-testid="player-fullscreen" title="Fullscreen">
            {fullscreen ? <CornersIn size={20} weight="regular" /> : <CornersOut size={20} weight="regular" />}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Camera card ─────────────────────────────────────────────────────── */
function CameraCard({ cam, storeId, isSuperAdmin, onRemoved }) {
  const [loading, setLoading] = useState(false);
  const [hlsUrl, setHlsUrl] = useState('');
  const [streamErr, setStreamErr] = useState('');
  const [online, setOnline] = useState(cam.online);

  const startStream = useCallback(async () => {
    setLoading(true);
    setStreamErr('');
    setHlsUrl('');
    try {
      const { data } = await axios.get(
        `${API_URL}/api/stores/${storeId}/cameras/${cam.id}/live`,
        { headers: authHeaders() },
      );
      // Prefer the HTTPS proxy URL when available (production HTTPS pages
      // cannot load Imou's plain-HTTP CDN directly — mixed-content block).
      setHlsUrl(data.hls_proxy_url || data.hls_url);
      setOnline(data.online ?? online);
    } catch (e) {
      const msg = formatApiError(e);
      setStreamErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [storeId, cam.id, online]);

  const stopStream = () => {
    setHlsUrl('');
    setStreamErr('');
  };

  const remove = async () => {
    if (!window.confirm(`Remove camera "${cam.label}"?`)) return;
    try {
      await axios.delete(
        `${API_URL}/api/stores/${storeId}/cameras/${cam.id}`,
        { headers: authHeaders() },
      );
      toast.success('Camera removed');
      onRemoved?.(cam.id);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onlineKnown = online === '1' || online === '0';
  const isOnline = online === '1';

  return (
    <div
      className="bg-white border border-[#EDE6D8] rounded-2xl p-4 space-y-3"
      data-testid={`camera-card-${cam.id}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-[#0A2540]/5 flex items-center justify-center shrink-0">
            <VideoCamera size={18} weight="duotone" className="text-[#0A2540]" />
          </div>
          <div className="min-w-0">
            <p
              className="text-sm font-semibold text-[#1A1D1C] truncate"
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
            >
              {cam.label || `Camera ${cam.imou_device_id?.slice(-4)}`}
            </p>
            <p
              className="text-[10px] uppercase tracking-widest text-[#78716C] truncate"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}
            >
              {cam.imou_device_id}
              {cam.channel_id != null && (
                <span className="ml-2 px-1.5 py-0.5 rounded bg-[#0A2540]/10 text-[#0A2540] font-semibold">
                  Ch {String(cam.channel_id)}
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {onlineKnown ? (
            isOnline ? (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#10B981]">
                <WifiHigh size={12} weight="bold" /> Online
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#EF4444]">
                <WifiSlash size={12} weight="bold" /> Offline
              </span>
            )
          ) : null}
          {isSuperAdmin && (
            <button
              type="button"
              onClick={remove}
              data-testid={`remove-camera-${cam.id}`}
              className="text-[#78716C] hover:text-[#EF4444] p-1"
              title="Remove camera"
            >
              <Trash size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Player or placeholder */}
      {hlsUrl ? (
        <>
          <HlsPlayer hlsUrl={hlsUrl} onError={(m) => setStreamErr(m)} onRefresh={startStream} />
          <div className="flex items-center justify-between">
            <p className="text-[10px] text-[#78716C]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              LIVE · HLS · stream {cam.stream_id}
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={stopStream}
              data-testid={`stop-stream-${cam.id}`}
              className="text-xs"
            >
              Stop
            </Button>
          </div>
        </>
      ) : (
        <div className="aspect-video rounded-xl bg-[#0A2540]/5 border border-dashed border-[#0A2540]/15 flex flex-col items-center justify-center gap-3">
          {loading ? (
            <>
              <Spinner size={24} className="animate-spin text-[#0A2540]" />
              <p className="text-xs text-[#78716C]">Starting live stream…</p>
            </>
          ) : streamErr ? (
            <>
              <WarningCircle size={24} className="text-[#EF4444]" />
              <p className="text-xs text-[#EF4444] max-w-[80%] text-center px-2">{streamErr}</p>
              <Button size="sm" variant="outline" onClick={startStream} data-testid={`retry-stream-${cam.id}`}>
                <ArrowsClockwise size={12} className="mr-1" /> Retry
              </Button>
            </>
          ) : (
            <>
              <Eye size={28} weight="duotone" className="text-[#0A2540]/40" />
              <Button size="sm" onClick={startStream} data-testid={`start-stream-${cam.id}`}>
                <Play size={12} weight="fill" className="mr-1" /> Start live view
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Bind camera dialog (super admin only) ──────────────────────────── */
function AddCameraDialog({ open, onOpenChange, storeId, onAdded }) {
  const [imouDeviceId, setImouDeviceId] = useState('');
  const [label, setLabel] = useState('');
  const [streamId, setStreamId] = useState(1);
  const [channelId, setChannelId] = useState('0');
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setImouDeviceId('');
    setLabel('');
    setStreamId(1);
    setChannelId('0');
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!imouDeviceId.trim()) {
      toast.error('Imou device id is required');
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/stores/${storeId}/cameras`,
        {
          imou_device_id: imouDeviceId.trim(),
          label: label.trim() || null,
          channel_id: channelId,
          stream_id: streamId,
        },
        { headers: authHeaders() },
      );
      toast.success('Camera bound');
      onAdded?.(data);
      reset();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-w-md" data-testid="add-camera-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Bind Imou Camera</DialogTitle>
          <DialogDescription>
            The device must already be paired to your Imou developer account.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-[#78716C]">Imou Device Serial</Label>
            <Input
              value={imouDeviceId}
              onChange={(e) => setImouDeviceId(e.target.value)}
              placeholder="e.g. 6F0CE3CPAZ12345"
              required
              autoFocus
              data-testid="cam-device-id"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-[#78716C]">Label (optional)</Label>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Front door, Counter, Storage room…"
              data-testid="cam-label"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-[#78716C]">Lens / Channel</Label>
            <div className="flex gap-2">
              {['0', '1', '2', '3'].map((ch) => (
                <button
                  key={ch}
                  type="button"
                  onClick={() => setChannelId(ch)}
                  className={`flex-1 h-10 rounded-lg border text-xs font-medium transition ${
                    channelId === ch
                      ? 'bg-[#0A2540] text-white border-[#0A2540]'
                      : 'bg-white text-[#0A2540] border-[#E2E8E5] hover:border-[#0A2540]/30'
                  }`}
                  data-testid={`cam-channel-${ch}`}
                >
                  Ch {ch}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-[#78716C]">
              Dual-lens cameras expose each lens as a separate channel (typically <b>Ch 0</b> = fixed, <b>Ch 1</b> = PT). Bind each channel as its own camera to view both lenses.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-[#78716C]">Stream Quality</Label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setStreamId(0)}
                className={`flex-1 h-10 rounded-lg border text-xs font-medium transition ${
                  streamId === 0
                    ? 'bg-[#0A2540] text-white border-[#0A2540]'
                    : 'bg-white text-[#0A2540] border-[#E2E8E5] hover:border-[#0A2540]/30'
                }`}
                data-testid="cam-stream-hd"
              >
                HD (main)
              </button>
              <button
                type="button"
                onClick={() => setStreamId(1)}
                className={`flex-1 h-10 rounded-lg border text-xs font-medium transition ${
                  streamId === 1
                    ? 'bg-[#0A2540] text-white border-[#0A2540]'
                    : 'bg-white text-[#0A2540] border-[#E2E8E5] hover:border-[#0A2540]/30'
                }`}
                data-testid="cam-stream-sd"
              >
                SD (sub)
              </button>
            </div>
            <p className="text-[10px] text-[#78716C]">SD uses less bandwidth; HD is best on stable connections.</p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="cam-cancel">
              Cancel
            </Button>
            <Button type="submit" disabled={saving} data-testid="cam-submit">
              {saving ? 'Binding…' : 'Bind Camera'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ─── Main panel ──────────────────────────────────────────────────────── */
export default function SurveillancePanel({ storeId }) {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [status, setStatus] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        axios.get(`${API_URL}/api/surveillance/status`, { headers: authHeaders() }),
        axios.get(`${API_URL}/api/stores/${storeId}/cameras`, { headers: authHeaders() }),
      ]);
      setStatus(s.data);
      setCameras(c.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="surveillance-panel">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3
            className="text-base font-semibold text-[#1A1D1C]"
            style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
          >
            Surveillance
          </h3>
          <p
            className="text-[10px] uppercase tracking-widest text-[#78716C] mt-0.5"
            style={{ fontFamily: 'JetBrains Mono, monospace' }}
          >
            Live cameras
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="surveillance-refresh">
            <ArrowsClockwise size={12} className="mr-1" /> Refresh
          </Button>
          {isSuperAdmin && (
            <Button size="sm" onClick={() => setAddOpen(true)} data-testid="add-camera-btn">
              <Plus size={12} className="mr-1" /> Add Camera
            </Button>
          )}
        </div>
      </div>

      {/* Status banner */}
      {status?.configured && !status?.writes_enabled && (
        <div
          className="rounded-xl border border-amber-300/40 bg-amber-50 text-amber-900 px-4 py-3 text-xs flex items-start gap-2"
          data-testid="imou-writes-disabled"
        >
          <WarningCircle size={16} weight="duotone" className="mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold mb-0.5">Live streaming is paused</p>
            <p>
              Middleware writes are disabled on this environment. Enable them
              from System Settings to start the cloud live stream.
            </p>
          </div>
        </div>
      )}

      {/* Body */}
      {loading ? (
        <div className="rounded-xl border border-dashed border-[#EDE6D8] py-12 flex items-center justify-center text-xs text-[#78716C]">
          <Spinner size={16} className="animate-spin mr-2" /> Loading cameras…
        </div>
      ) : cameras.length === 0 ? (
        <div
          className="rounded-2xl border border-dashed border-[#EDE6D8] py-16 px-6 flex flex-col items-center justify-center text-center gap-3"
          data-testid="surveillance-empty"
        >
          <VideoCamera size={32} weight="duotone" className="text-[#0A2540]/30" />
          <p className="text-sm font-medium text-[#1A1D1C]">No cameras bound yet</p>
          <p className="text-xs text-[#78716C] max-w-sm">
            {isSuperAdmin
              ? 'Bind an Imou camera to view live activity at this store. The camera must already be paired to your Imou developer account.'
              : 'Cameras for this store will appear here once a super admin binds them.'}
          </p>
          {isSuperAdmin && (
            <Button size="sm" onClick={() => setAddOpen(true)} data-testid="surveillance-empty-add">
              <Plus size={12} className="mr-1" /> Add the first camera
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {cameras.map((cam) => (
            <CameraCard
              key={cam.id}
              cam={cam}
              storeId={storeId}
              isSuperAdmin={isSuperAdmin}
              onRemoved={(camId) =>
                setCameras((prev) => prev.filter((c) => c.id !== camId))
              }
            />
          ))}
        </div>
      )}

      <AddCameraDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        storeId={storeId}
        onAdded={(cam) => setCameras((prev) => [...prev, cam])}
      />
    </div>
  );
}
